import type * as pc from "playcanvas";
import type { GameClient, RemotePlayerState } from "../net/GameClient";
import type { FirstPersonController } from "../player/FirstPersonController";
import { MOVEMENT_FPS } from "@hupper/shared";

/**
 * SpectatorCam — Sprint 6.
 *
 * When the local player's health drops to 0 we detach the FPC camera
 * from its body and slide it to the survivor's head, locking onto their
 * yaw/pitch so the dead player can see what's happening. Once
 * round_end fires and respawn snapshots arrive, the FPC takes over
 * again at the spawn point and the spectator camera disengages.
 *
 * 1v1 has only one possible target — no carousel cycling needed.
 */
export class SpectatorCam {
  private readonly app: pc.AppBase;
  private readonly client: GameClient;
  private readonly fpc: FirstPersonController;
  private readonly cameraEntity: pc.Entity;
  private readonly bannerEl: HTMLDivElement;
  private readonly unsubs: Array<() => void> = [];
  private active = false;
  private targetSid: string | null = null;
  private readonly tickFn: (dt: number) => void;

  constructor(app: pc.AppBase, client: GameClient, fpc: FirstPersonController) {
    this.app = app;
    this.client = client;
    this.fpc = fpc;
    this.cameraEntity = fpc.getCameraEntity();

    this.bannerEl = document.createElement("div");
    this.bannerEl.id = "spectator-banner";
    this.bannerEl.classList.add("hidden");
    this.bannerEl.innerHTML = `<span class="spec-dot"></span><span class="spec-text">Spectating</span><span class="spec-target">—</span>`;
    document.body.appendChild(this.bannerEl);
    this.injectStyles();

    this.tickFn = (dt: number) => this.update(dt);
    this.app.on("update", this.tickFn);

    this.unsubs.push(this.client.onPlayerChange((p, sid) => {
      if (sid === this.client.sessionId) this.onSelfChange(p);
    }));
    this.unsubs.push(this.client.onRoundEnd(() => this.disengage()));
  }

  destroy(): void {
    this.app.off("update", this.tickFn);
    for (const u of this.unsubs) u();
    this.bannerEl.remove();
    document.body.classList.remove("spectating");
  }

  private onSelfChange(p: RemotePlayerState): void {
    if (p.health <= 0 && !this.active) {
      this.engage();
    } else if (p.health > 0 && this.active) {
      this.disengage();
    }
  }

  private engage(): void {
    const survivor = this.findSurvivor();
    if (!survivor) return; // No one to spectate (e.g. round end about to fire) — bail.
    this.active = true;
    this.targetSid = survivor.sessionId;
    const tag = survivor.team === "ct" ? "CT" : "T";
    this.bannerEl.innerHTML = `
      <span class="spec-dot spec-${survivor.team ?? "ct"}"></span>
      <span class="spec-text">Spectating</span>
      <span class="spec-target spec-${survivor.team ?? "ct"}">${escapeHtml(survivor.name)}</span>
      <span class="spec-tag spec-${survivor.team ?? "ct"}">${tag}</span>
    `;
    this.bannerEl.classList.remove("hidden");
    // Hide the local-player HUD (health, armor, ammo, crosshair…) while
    // spectating — those read our own dead state and are meaningless when
    // we're watching someone else. One body class drives the CSS below.
    document.body.classList.add("spectating");
    // Restart the entrance animation each time we engage a new target.
    this.bannerEl.style.animation = "none";
    void this.bannerEl.offsetWidth;
    this.bannerEl.style.animation = "";
  }

  private disengage(): void {
    if (!this.active) return;
    this.active = false;
    this.targetSid = null;
    this.bannerEl.classList.add("hidden");
    document.body.classList.remove("spectating");
  }

  private findSurvivor(): RemotePlayerState | null {
    const selfSid = this.client.sessionId;
    // Pull the cached known map indirectly via getPlayerSnapshot. Iterate
    // over the lobby's expected ids by walking known sessions in lastHud
    // is impractical; the cleanest way is to introspect the colyseus
    // delta — we don't have a public iterator, so we manually probe by
    // checking the only other session via the snapshot field on `team`.
    // Simpler: track snapshots via onPlayerChange in a tiny cache. Skip
    // that for now and just rely on the snapshot field — the GameClient
    // exposes the local one. To grab the opponent, we walk `known`
    // through `getPlayerSnapshot` on every known session id.
    // The auxiliary getter `getPlayerSnapshot(sid)` returns null for
    // unknown ids, so we just probe both expected sessions if we have
    // them cached. As a fallback, iterate over a (small) set obtained
    // from a public method we now expose: `forEachKnownPlayer`.
    let candidate: RemotePlayerState | null = null;
    this.client.forEachKnownPlayer((p) => {
      if (p.sessionId === selfSid) return;
      if (p.health <= 0) return;
      candidate = p;
    });
    return candidate;
  }

  private update(_dt: number): void {
    if (!this.active || !this.targetSid) return;
    const target = this.client.getPlayerSnapshot(this.targetSid);
    if (!target || target.health <= 0) {
      this.disengage();
      return;
    }
    // Slide the camera to the survivor's eye position + match their yaw
    // (pitch we copy too so head movement reads).
    const eye = target.crouching ? MOVEMENT_FPS.EYE_HEIGHT_CROUCH : MOVEMENT_FPS.EYE_HEIGHT_STANDING;
    const tx = target.x;
    const ty = target.y + eye;
    const tz = target.z;
    // Detach from FPC body for the duration of spectate — set absolute
    // world position on the camera entity directly.
    this.cameraEntity.setPosition(tx, ty, tz);
    this.cameraEntity.setEulerAngles(target.pitch, target.yaw, 0);
  }

  private injectStyles(): void {
    if (document.getElementById("spectator-banner-styles")) return;
    const style = document.createElement("style");
    style.id = "spectator-banner-styles";
    style.textContent = `
      /* While spectating, hide every local-player HUD widget — our own
         health, armor, ammo, crosshair and pickup prompt are meaningless
         when we're watching the survivor. Match info (scoreboard, kill
         feed) and the spectator banner itself stay. */
      body.spectating #hud-bottom-left,
      body.spectating #weapon-hud,
      body.spectating #crosshair,
      body.spectating #pickup-prompt {
        display: none !important;
      }
      #spectator-banner {
        position: fixed;
        top: 56px;
        left: 50%;
        transform: translateX(-50%);
        z-index: 9;
        pointer-events: none;
        display: inline-flex;
        align-items: center;
        gap: 9px;
        padding: 9px 16px;
        border-radius: 999px;
        border: 1px solid rgba(255, 255, 255, 0.10);
        background: linear-gradient(180deg, rgba(22, 24, 30, 0.82), rgba(12, 13, 17, 0.88));
        backdrop-filter: blur(14px) saturate(1.2);
        -webkit-backdrop-filter: blur(14px) saturate(1.2);
        box-shadow: 0 8px 28px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.06);
        font-family: ui-sans-serif, system-ui, sans-serif;
        font-size: 13px;
        color: var(--text, #f5f5f0);
        animation: spec-banner-in 360ms cubic-bezier(0.16, 1, 0.3, 1);
      }
      #spectator-banner.hidden { display: none; }
      @keyframes spec-banner-in {
        from { opacity: 0; transform: translateX(-50%) translateY(-8px) scale(0.96); }
        to   { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
      }
      #spectator-banner .spec-dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: currentColor;
        color: var(--text-mute, #8a8a92);
        box-shadow: 0 0 8px currentColor;
        animation: spec-pulse 1.6s ease-in-out infinite;
      }
      @keyframes spec-pulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50%      { opacity: 0.45; transform: scale(0.82); }
      }
      #spectator-banner .spec-text {
        font-family: ui-monospace, SFMono-Regular, monospace;
        font-size: 10px;
        font-weight: 600;
        letter-spacing: 0.22em;
        text-transform: uppercase;
        color: var(--text-mute, #8a8a92);
      }
      #spectator-banner .spec-target {
        font-weight: 700;
        letter-spacing: 0.01em;
      }
      #spectator-banner .spec-tag {
        font-family: ui-monospace, SFMono-Regular, monospace;
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 0.08em;
        padding: 2px 6px;
        border-radius: 5px;
        color: #0b0c10;
      }
      #spectator-banner .spec-tag.spec-ct { background: #5eb8ff; }
      #spectator-banner .spec-tag.spec-t  { background: #ff8a5c; }
      #spectator-banner .spec-ct { color: #5eb8ff; }
      #spectator-banner .spec-t  { color: #ff8a5c; }
    `;
    document.head.appendChild(style);
  }
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
